import React, { useState, useEffect } from "react";
import {
  Box,
  HStack,
  VStack,
  Text,
  Button,
  Pressable,
  Image,
  Center,
  ScrollView,
  Input,
  Radio,
  Spinner,
  useToast,
  Stack,
  Modal,
  Select,
} from "native-base";
import { GlobalStyles } from "../../constants/styles";
import axios from "axios";
import { GlobalConstants } from "../../constants/constants";
import { useSelector } from "react-redux";

// const buildApiUrl = (baseUrl, port, endpoint) => {
//   const clean = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
//   return `${clean}:${port}${endpoint}`;
// };
// const API_BASE_URL = buildApiUrl(
//   GlobalConstants.servicesBaseUrl,
//   "3002",
//   "/api",
// );
const API_BASE_URL = `${GlobalConstants.SubscriptionsBaseUrl}`.replace(/\/$/, "") + "/api";

const SERVICE_OPTIONS = [
  "loyalty",
  "reservation",
  "coupon",
  "survey",
  "ticket",
  "email",
];

const SERVICE_LABELS = {
  loyalty: "Loyalty Points",
  reservation: "Reservation System",
  coupon: "Coupons",
  survey: "Survey",
  ticket: "Ticketing System",
  email: "Customer Data & Email",
};

const durationToLabel = (days) => {
  if (days <= 30) return "1 Month";
  if (days <= 180) return "6 Months";
  return "1 Year";
};

const showToast = (toast, message, isError = false) => {
  toast.show({
    render: () => (
      <Box
        bg={isError ? GlobalStyles.colors.red : GlobalStyles.colors.green}
        px={3}
        py={2}
        borderRadius={6}
      >
        <Text
          color={GlobalStyles.colors.white}
          fontFamily={GlobalStyles.text.fontFamily}
        >
          {message}
        </Text>
      </Box>
    ),
    placement: "top",
  });
};

const PlanCards = ({ plans, isLoading, selectedPlanId, onSelectPlan }) => {
  if (isLoading)
    return (
      <HStack space={2} alignItems="center" py={2}>
        <Spinner size="sm" color={GlobalStyles.colors.blue} />
        <Text fontSize="12px" fontFamily={GlobalStyles.text.fontFamily}>
          Loading plans...
        </Text>
      </HStack>
    );
  if (!plans || plans.length === 0)
    return (
      <Text fontSize="12px" color="gray.500">
        No plans available
      </Text>
    );
  return (
    <HStack space={2} flexWrap="wrap" mt={1}>
      {[...plans]
        .sort((a, b) => a.duration - b.duration)
        .map((plan) => {
          const isSelected = selectedPlanId === plan.id;
          return (
            <Pressable
              key={plan.id}
              onPress={() =>
                onSelectPlan && onSelectPlan(isSelected ? null : plan)
              }
            >
              <Box
                borderWidth={2}
                borderColor={
                  isSelected
                    ? GlobalStyles.colors.mustard
                    : GlobalStyles.colors.blue
                }
                borderRadius={8}
                bg={isSelected ? "#FFFBF0" : "white"}
                px={4}
                py={2}
                mb={2}
                alignItems="center"
                minW="80px"
              >
                <Text
                  fontWeight="bold"
                  fontSize="12px"
                  fontFamily={GlobalStyles.text.fontFamily}
                  color={
                    isSelected
                      ? GlobalStyles.colors.mustard
                      : GlobalStyles.colors.blue
                  }
                >
                  {durationToLabel(plan.duration)}
                </Text>
                <Text
                  fontSize="11px"
                  fontFamily={GlobalStyles.text.fontFamily}
                  color={
                    isSelected
                      ? GlobalStyles.colors.mustard
                      : GlobalStyles.colors.blue
                  }
                >
                  {parseFloat(plan.price).toFixed(0)} USD
                </Text>
              </Box>
            </Pressable>
          );
        })}
    </HStack>
  );
};

const AlreadySubscribedModal = ({
  isOpen,
  onClose,
  onProceed,
  serviceName,
}) => (
  <Modal isOpen={isOpen} onClose={onClose} size="sm">
    <Modal.Content maxWidth="340px" borderRadius={12}>
      <Modal.CloseButton />
      <Box bg={GlobalStyles.colors.blue} px={5} py={4}>
        <Text
          fontSize="14px"
          fontWeight="700"
          color="white"
          fontFamily={GlobalStyles.text.fontFamily}
        >
          Already Subscribed
        </Text>
      </Box>
      <Modal.Body px={5} py={4}>
        <Text
          fontFamily={GlobalStyles.text.fontFamily}
          fontSize="13px"
          color="#333"
          textAlign="center"
        >
          You already have an active subscription for{" "}
          <Text fontWeight="700" color={GlobalStyles.colors.blue}>
            {serviceName}
          </Text>
          .{"\n\n"}Do you still want to subscribe again?
        </Text>
      </Modal.Body>
      <Box px={4} py={3} borderTopWidth={1} borderTopColor="#EEF0F4">
        <HStack space={2}>
          <Button
            flex={1}
            variant="outline"
            borderColor={GlobalStyles.colors.blue}
            borderRadius={8}
            onPress={onClose}
            h="38px"
            _text={{
              color: GlobalStyles.colors.blue,
              fontSize: "13px",
              fontWeight: "600",
              fontFamily: GlobalStyles.text.fontFamily,
            }}
          >
            Cancel
          </Button>
          <Button
            flex={2}
            bg={GlobalStyles.colors.blue}
            borderRadius={8}
            onPress={onProceed}
            h="38px"
            _text={{
              color: "white",
              fontSize: "13px",
              fontWeight: "700",
              fontFamily: GlobalStyles.text.fontFamily,
            }}
            _pressed={{ bg: "#001544" }}
          >
            Subscribe Anyway
          </Button>
        </HStack>
      </Box>
    </Modal.Content>
  </Modal>
);

const ServicePaymentModal = ({
  isOpen,
  onClose,
  onSuccess,
  loggedInUser,
  preselectedService = "",
}) => {
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [selectedFixedPlan, setSelectedFixedPlan] = useState(null);
  const [selectedService, setSelectedService] = useState(preselectedService);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardHolder, setCardHolder] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvv, setCvv] = useState("");
  const [cardType, setCardType] = useState("");
  // Duplicate subscription warning
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [existingSubscriptions, setExistingSubscriptions] = useState([]);
  const toast = useToast();
  const userId = loggedInUser?.id || loggedInUser?.user_id || null;
  const userEmail = loggedInUser?.email || "";

  useEffect(() => {
    if (isOpen) {
      fetchPlans();
      fetchUserSubscriptions();
      setSelectedFixedPlan(null);
      setSelectedService(preselectedService || "");
      setCardHolder("");
      setCardNumber("");
      setExpiry("");
      setCvv("");
      setCardType("");
      setShowDuplicateWarning(false);
    }
  }, [isOpen, preselectedService]);

  const fetchPlans = async () => {
    try {
      setPlansLoading(true);
      const res = await axios.get(`${API_BASE_URL}/plans`);
      setPlans(res.data);
    } catch {
      setPlans([]);
    } finally {
      setPlansLoading(false);
    }
  };

  const fetchUserSubscriptions = async () => {
    if (!userId) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/subscriptions/user/${userId}`,
      );
      const data = Array.isArray(res.data) ? res.data : [];
      setExistingSubscriptions(data.filter((s) => s.status === "ACTIVE"));
    } catch {
      setExistingSubscriptions([]);
    }
  };

  const hasActiveSubscriptionForService = (service) => {
    if (!service) return false;
    return existingSubscriptions.some(
      (s) => s.service === service && s.status === "ACTIVE",
    );
  };

  const handleConfirmClick = () => {
    if (!userId) {
      showToast(toast, "User not found. Please log in again.", true);
      return;
    }
    if (!selectedFixedPlan) {
      showToast(toast, "Please select a plan.", true);
      return;
    }
    if (!selectedService) {
      showToast(toast, "Please select a service.", true);
      return;
    }

    if (hasActiveSubscriptionForService(selectedService)) {
      setShowDuplicateWarning(true);
      return;
    }

    proceedWithPayment();
  };

  const proceedWithPayment = async () => {
    setShowDuplicateWarning(false);
    try {
      setIsProcessing(true);
      const startDate = new Date();
      const months = Math.max(1, Math.round(selectedFixedPlan.duration / 30));
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + months);
      await axios.post(`${API_BASE_URL}/subscriptions`, {
        user_id: userId,
        plan_id: selectedFixedPlan.id,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        status: "ACTIVE",
        service: selectedService,
      });
      showToast(
        toast,
        `Subscribed to ${SERVICE_LABELS[selectedService] || selectedService}!`,
      );
      setIsProcessing(false);
      onSuccess && onSuccess(selectedService);
      onClose();
    } catch {
      showToast(
        toast,
        "Failed to create subscription. Please try again.",
        true,
      );
      setIsProcessing(false);
    }
  };

  const total = selectedFixedPlan
    ? parseFloat(selectedFixedPlan.price).toFixed(2)
    : "0.00";
  const serviceName = selectedService
    ? SERVICE_LABELS[selectedService] || selectedService
    : "—";

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <Modal.Content maxWidth="480px" borderRadius={14} overflow="hidden">
          <Modal.CloseButton />

          <Box bg={GlobalStyles.colors.blue} px={5} py={4}>
            <Text
              fontSize="15px"
              fontWeight="700"
              color="white"
              fontFamily={GlobalStyles.text.fontFamily}
            >
              Subscribe to a Service
            </Text>
            <Text
              fontSize="11px"
              color="#A8BBDA"
              fontFamily={GlobalStyles.text.fontFamily}
              mt={0.5}
            >
              Secure payment — your subscription starts immediately
            </Text>
          </Box>

          <Modal.Body p={0}>
            <ScrollView showsVerticalScrollIndicator={false}>
              <HStack>
                <VStack
                  flex={1}
                  px={4}
                  py={4}
                  space={3}
                  borderRightWidth={1}
                  borderRightColor="#EEF0F4"
                >
                  <VStack space={1}>
                    <Text
                      fontSize="11px"
                      fontWeight="600"
                      color={GlobalStyles.colors.blue}
                      fontFamily={GlobalStyles.text.fontFamily}
                      textTransform="uppercase"
                      letterSpacing={0.5}
                    >
                      Service
                    </Text>
                    <Select
                      selectedValue={selectedService}
                      onValueChange={setSelectedService}
                      placeholder="Choose service"
                      borderColor="#CBD5E0"
                      borderRadius={6}
                      fontSize="13px"
                      fontFamily={GlobalStyles.text.fontFamily}
                      h="38px"
                      isDisabled={!!preselectedService}
                      bg={preselectedService ? "#F7F9FC" : "white"}
                      _actionSheetContent={{ maxH: "200px" }}
                    >
                      {SERVICE_OPTIONS.map((s) => (
                        <Select.Item
                          key={s}
                          label={SERVICE_LABELS[s]}
                          value={s}
                        />
                      ))}
                    </Select>
                    {selectedService &&
                      hasActiveSubscriptionForService(selectedService) && (
                        <Box
                          bg="#FFF3CD"
                          borderColor="#FFC107"
                          borderWidth={1}
                          borderRadius={6}
                          px={3}
                          py={2}
                          mt={1}
                        >
                          <Text
                            fontSize="11px"
                            color="#856404"
                            fontFamily={GlobalStyles.text.fontFamily}
                          >
                            ⚠️ You already have an active subscription for{" "}
                            <Text fontWeight="700">
                              {SERVICE_LABELS[selectedService] ||
                                selectedService}
                            </Text>
                            . You can still subscribe again.
                          </Text>
                        </Box>
                      )}
                  </VStack>

                  <VStack space={1}>
                    <Text
                      fontSize="11px"
                      fontWeight="600"
                      color={GlobalStyles.colors.blue}
                      fontFamily={GlobalStyles.text.fontFamily}
                      textTransform="uppercase"
                      letterSpacing={0.5}
                    >
                      Plan
                    </Text>
                    <PlanCards
                      plans={plans}
                      isLoading={plansLoading}
                      selectedPlanId={selectedFixedPlan?.id}
                      onSelectPlan={setSelectedFixedPlan}
                    />
                  </VStack>

                  <VStack space={1}>
                    <Text
                      fontSize="11px"
                      fontWeight="600"
                      color={GlobalStyles.colors.blue}
                      fontFamily={GlobalStyles.text.fontFamily}
                      textTransform="uppercase"
                      letterSpacing={0.5}
                    >
                      Card Details
                    </Text>
                    {[
                      {
                        val: cardHolder,
                        set: setCardHolder,
                        placeholder: "Card holder name",
                      },
                      {
                        val: cardNumber,
                        set: setCardNumber,
                        placeholder: "Card number",
                        numeric: true,
                        max: 16,
                      },
                      {
                        val: cardType,
                        set: setCardType,
                        placeholder: "Card type (Visa / Mastercard)",
                      },
                    ].map(({ val, set, placeholder, numeric, max }) => (
                      <Input
                        key={placeholder}
                        value={val}
                        onChangeText={set}
                        placeholder={placeholder}
                        keyboardType={numeric ? "numeric" : "default"}
                        maxLength={max}
                        h="36px"
                        fontSize="12px"
                        fontFamily={GlobalStyles.text.fontFamily}
                        borderColor="#CBD5E0"
                        borderRadius={6}
                        bg="white"
                      />
                    ))}
                    <HStack space={2}>
                      <Input
                        flex={1}
                        value={expiry}
                        onChangeText={setExpiry}
                        placeholder="MM/YY"
                        maxLength={5}
                        h="36px"
                        fontSize="12px"
                        fontFamily={GlobalStyles.text.fontFamily}
                        borderColor="#CBD5E0"
                        borderRadius={6}
                        bg="white"
                      />
                      <Input
                        flex={1}
                        value={cvv}
                        onChangeText={setCvv}
                        placeholder="CVV"
                        keyboardType="numeric"
                        maxLength={3}
                        secureTextEntry
                        h="36px"
                        fontSize="12px"
                        fontFamily={GlobalStyles.text.fontFamily}
                        borderColor="#CBD5E0"
                        borderRadius={6}
                        bg="white"
                      />
                    </HStack>
                  </VStack>
                </VStack>

                <VStack
                  w="140px"
                  px={3}
                  py={4}
                  bg="#F7F9FC"
                  justifyContent="space-between"
                >
                  <VStack space={3}>
                    <Text
                      fontSize="11px"
                      fontWeight="700"
                      color={GlobalStyles.colors.blue}
                      fontFamily={GlobalStyles.text.fontFamily}
                      textTransform="uppercase"
                      letterSpacing={0.5}
                    >
                      Summary
                    </Text>
                    <VStack space={1}>
                      <Text
                        fontSize="10px"
                        color="gray.500"
                        fontFamily={GlobalStyles.text.fontFamily}
                      >
                        Service
                      </Text>
                      <Text
                        fontSize="12px"
                        fontWeight="600"
                        color={GlobalStyles.colors.blue}
                        fontFamily={GlobalStyles.text.fontFamily}
                        numberOfLines={2}
                      >
                        {serviceName}
                      </Text>
                    </VStack>
                    <VStack space={1}>
                      <Text
                        fontSize="10px"
                        color="gray.500"
                        fontFamily={GlobalStyles.text.fontFamily}
                      >
                        Plan
                      </Text>
                      <Text
                        fontSize="12px"
                        fontWeight="600"
                        color={GlobalStyles.colors.blue}
                        fontFamily={GlobalStyles.text.fontFamily}
                      >
                        {selectedFixedPlan
                          ? durationToLabel(selectedFixedPlan.duration)
                          : "—"}
                      </Text>
                    </VStack>
                    {userEmail ? (
                      <VStack space={1}>
                        <Text
                          fontSize="10px"
                          color="gray.500"
                          fontFamily={GlobalStyles.text.fontFamily}
                        >
                          Account
                        </Text>
                        <Text
                          fontSize="10px"
                          color="gray.600"
                          fontFamily={GlobalStyles.text.fontFamily}
                          numberOfLines={2}
                        >
                          {userEmail}
                        </Text>
                      </VStack>
                    ) : null}
                    <Box
                      borderTopWidth={1}
                      borderTopColor="#DDE2EB"
                      pt={2}
                      mt={1}
                    >
                      <Text
                        fontSize="10px"
                        color="gray.500"
                        fontFamily={GlobalStyles.text.fontFamily}
                      >
                        Total
                      </Text>
                      <Text
                        fontSize="18px"
                        fontWeight="700"
                        color={GlobalStyles.colors.mustard}
                        fontFamily={GlobalStyles.text.fontFamily}
                      >
                        ${total}
                      </Text>
                      <Text
                        fontSize="9px"
                        color="gray.400"
                        fontFamily={GlobalStyles.text.fontFamily}
                      >
                        USD
                      </Text>
                    </Box>
                  </VStack>
                  <Box mt={3} alignItems="center">
                    <Box
                      w="28px"
                      h="3px"
                      bg={GlobalStyles.colors.mustard}
                      borderRadius={2}
                      mb={1}
                    />
                    <Text
                      fontSize="9px"
                      color="gray.400"
                      fontFamily={GlobalStyles.text.fontFamily}
                      textAlign="center"
                    >
                      TownTicks{"\n"}Secure Pay
                    </Text>
                  </Box>
                </VStack>
              </HStack>
            </ScrollView>
          </Modal.Body>

          <Box
            px={4}
            py={3}
            borderTopWidth={1}
            borderTopColor="#EEF0F4"
            bg="white"
          >
            <HStack space={2}>
              <Button
                flex={1}
                variant="outline"
                borderColor={GlobalStyles.colors.blue}
                borderRadius={8}
                onPress={onClose}
                isDisabled={isProcessing}
                h="38px"
                _text={{
                  color: GlobalStyles.colors.blue,
                  fontSize: "13px",
                  fontWeight: "600",
                  fontFamily: GlobalStyles.text.fontFamily,
                }}
              >
                Cancel
              </Button>
              <Button
                flex={2}
                bg={GlobalStyles.colors.blue}
                borderRadius={8}
                onPress={handleConfirmClick}
                isDisabled={
                  isProcessing || !selectedFixedPlan || !selectedService
                }
                h="38px"
                _text={{
                  color: "white",
                  fontSize: "13px",
                  fontWeight: "700",
                  fontFamily: GlobalStyles.text.fontFamily,
                }}
                _pressed={{ bg: "#001544" }}
              >
                {isProcessing ? (
                  <Spinner color="white" size="sm" />
                ) : (
                  "Confirm & Subscribe"
                )}
              </Button>
            </HStack>
          </Box>
        </Modal.Content>
      </Modal>

      <AlreadySubscribedModal
        isOpen={showDuplicateWarning}
        onClose={() => setShowDuplicateWarning(false)}
        serviceName={SERVICE_LABELS[selectedService] || selectedService}
        onProceed={proceedWithPayment}
      />
    </>
  );
};

const SubscriptionPlansTable = ({ onBack, onSubscribe }) => {
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const toast = useToast();
  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/plans`)
      .then((r) =>
        setPlans([...r.data].sort((a, b) => a.duration - b.duration)),
      )
      .catch(() => showToast(toast, "Failed to load plans.", true))
      .finally(() => setIsLoading(false));
  }, []);
  return (
    <VStack space={3} py={3}>
      <Pressable onPress={onBack}>
        <Text
          color={GlobalStyles.colors.blue}
          fontSize="13px"
          fontFamily={GlobalStyles.text.fontFamily}
        >
          ← Back
        </Text>
      </Pressable>
      <Text
        fontSize="14px"
        fontWeight="600"
        fontFamily={GlobalStyles.text.fontFamily}
        color={GlobalStyles.colors.blue}
      >
        Available Subscription Plans
      </Text>
      {isLoading ? (
        <HStack space={2} alignItems="center">
          <Spinner size="sm" color={GlobalStyles.colors.blue} />
          <Text fontSize="13px">Loading...</Text>
        </HStack>
      ) : plans.length === 0 ? (
        <Text fontFamily={GlobalStyles.text.fontFamily}>
          No plans available
        </Text>
      ) : (
        <Box
          borderWidth={1}
          borderColor={GlobalStyles.colors.tableBorder}
          w={["100%", "100%", "480px"]}
          alignSelf="flex-start"
        >
          <HStack
            borderBottomWidth={1}
            borderColor={GlobalStyles.colors.tableBorder}
          >
            {["Plan", "Duration", "Price (USD)", "Subscribe"].map((h, i) => (
              <Box
                key={h}
                flex={1}
                py={3}
                px={2}
                borderRightWidth={i < 3 ? 1 : 0}
                borderColor={GlobalStyles.colors.tableBorder}
                justifyContent="center"
              >
                <Text
                  fontWeight="600"
                  fontFamily={GlobalStyles.text.fontFamily}
                  fontSize="13px"
                  textAlign="center"
                >
                  {h}
                </Text>
              </Box>
            ))}
          </HStack>
          {plans.map((plan, idx) => (
            <HStack
              key={plan.id}
              borderBottomWidth={idx < plans.length - 1 ? 1 : 0}
              borderColor={GlobalStyles.colors.tableBorder}
            >
              <Box
                flex={1}
                py={3}
                px={2}
                borderRightWidth={1}
                borderColor={GlobalStyles.colors.tableBorder}
                justifyContent="center"
              >
                <Text
                  fontFamily={GlobalStyles.text.fontFamily}
                  fontSize="13px"
                  textAlign="center"
                >
                  {plan.name || durationToLabel(plan.duration)}
                </Text>
              </Box>
              <Box
                flex={1}
                py={3}
                px={2}
                borderRightWidth={1}
                borderColor={GlobalStyles.colors.tableBorder}
                justifyContent="center"
              >
                <Text
                  fontFamily={GlobalStyles.text.fontFamily}
                  fontSize="13px"
                  textAlign="center"
                >
                  {durationToLabel(plan.duration)}
                </Text>
              </Box>
              <Box
                flex={1}
                py={3}
                px={2}
                borderRightWidth={1}
                borderColor={GlobalStyles.colors.tableBorder}
                justifyContent="center"
              >
                <Text
                  fontFamily={GlobalStyles.text.fontFamily}
                  fontSize="13px"
                  textAlign="center"
                >
                  {parseFloat(plan.price).toFixed(0)}
                </Text>
              </Box>
              <Box
                flex={1}
                py={3}
                px={2}
                justifyContent="center"
                alignItems="center"
              >
                <Pressable onPress={() => onSubscribe && onSubscribe(plan)}>
                  <Text
                    color={GlobalStyles.colors.linkBlue}
                    fontFamily={GlobalStyles.text.fontFamily}
                    fontSize="13px"
                  >
                    Subscribe
                  </Text>
                </Pressable>
              </Box>
            </HStack>
          ))}
        </Box>
      )}
    </VStack>
  );
};


const AccountPaymentsTable = ({ onSubscribePlan }) => {
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const toast = useToast();

  const loggedInUser = useSelector((state) => state.user.user);
  const userId = loggedInUser?.id || loggedInUser?.user_id || null;

  useEffect(() => {
    fetchPaymentHistory();
  }, [userId]);

  const fetchPaymentHistory = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${API_BASE_URL}/invoices`);
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      const filtered = userId
        ? data.filter(
            (inv) =>
              String(inv.user_id) === String(userId) ||
              String(inv.userId) === String(userId) ||
              inv.user?.id === userId,
          )
        : data;
      setInvoices(filtered);
    } catch {
      setInvoices([]);
      showToast(toast, "Failed to load payment history.", true);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchInvoiceDetails = async (id) => {
    try {
      const r = await axios.get(`${API_BASE_URL}/invoices/${id}`);
      setSelectedInvoice(r.data);
      setShowInvoiceModal(true);
    } catch {
      showToast(toast, "Failed to load invoice.", true);
    }
  };

  const formatDate = (ds) => {
    if (!ds) return "—";
    return new Date(ds)
      .toLocaleDateString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .toUpperCase();
  };

  const getInvoiceServiceName = (inv) => {
    if (inv.service) return SERVICE_LABELS[inv.service] || inv.service;
    if (inv.Subscription?.service)
      return (
        SERVICE_LABELS[inv.Subscription.service] || inv.Subscription.service
      );
    if (inv.subscription?.service)
      return (
        SERVICE_LABELS[inv.subscription.service] || inv.subscription.service
      );
    if (inv.plan_name || inv.planName) return inv.plan_name || inv.planName;
    if (inv.subscription_id) return `Sub #${inv.subscription_id}`;
    return "—";
  };

  if (activeSection === "make_payment") {
    return (
      <PaymentForm
        service={null}
        onConfirm={() => {
          setActiveSection(null);
          fetchPaymentHistory();
        }}
        onBack={() => setActiveSection(null)}
        preselectedPlan={null}
        loggedInUser={loggedInUser}
      />
    );
  }
  if (activeSection === "subscription_plan") {
    return (
      <SubscriptionPlansTable
        onBack={() => setActiveSection(null)}
        onSubscribe={(plan) => {
          onSubscribePlan && onSubscribePlan(plan);
        }}
      />
    );
  }

  return (
    <>
      <HStack space={2} mb={4} flexWrap="wrap">
        <Button
          bg={GlobalStyles.colors.blue}
          borderRadius={8}
          px={4}
          py={2}
          onPress={() => setActiveSection("make_payment")}
          _text={{
            color: "white",
            fontSize: "12px",
            fontWeight: "600",
            fontFamily: GlobalStyles.text.fontFamily,
          }}
        >
          Make Payment
        </Button>
        <Button
          variant="outline"
          borderColor={GlobalStyles.colors.blue}
          borderWidth={1.5}
          borderRadius={8}
          px={4}
          py={2}
          bg="transparent"
          onPress={() => setActiveSection("subscription_plan")}
          _text={{
            color: GlobalStyles.colors.blue,
            fontSize: "12px",
            fontWeight: "600",
            fontFamily: GlobalStyles.text.fontFamily,
          }}
        >
          Subscription Plan
        </Button>
      </HStack>

      <Text
        fontSize="13px"
        fontWeight="600"
        fontFamily={GlobalStyles.text.fontFamily}
        color={GlobalStyles.colors.blue}
        mb={2}
      >
        Payment History
      </Text>

      {isLoading ? (
        <HStack space={2} alignItems="center" py={4}>
          <Spinner size="sm" color={GlobalStyles.colors.blue} />
          <Text fontSize="13px" fontFamily={GlobalStyles.text.fontFamily}>
            Loading payments...
          </Text>
        </HStack>
      ) : invoices.length === 0 ? (
        <Box
          borderWidth={1}
          borderColor={GlobalStyles.colors.tableBorder}
          p={4}
        >
          <Text
            textAlign="center"
            fontFamily={GlobalStyles.text.fontFamily}
            color="gray.500"
          >
            No payment history available
          </Text>
        </Box>
      ) : (
        <Box
          borderWidth={1}
          borderColor={GlobalStyles.colors.tableBorder}
          w="100%"
        >
          <HStack
            borderBottomWidth={1}
            borderColor={GlobalStyles.colors.tableBorder}
          >
            {["Service", "Amount", "Date", "Receipt", "Status"].map((h, i) => (
              <Box
                key={h}
                flex={1}
                py={3}
                px={2}
                borderRightWidth={i < 4 ? 1 : 0}
                borderColor={GlobalStyles.colors.tableBorder}
                justifyContent="center"
              >
                <Text
                  fontWeight="600"
                  fontFamily={GlobalStyles.text.fontFamily}
                  fontSize="12px"
                  textAlign="center"
                >
                  {h}
                </Text>
              </Box>
            ))}
          </HStack>
          {invoices.map((inv, idx) => (
            <HStack
              key={inv.id || idx}
              borderBottomWidth={idx < invoices.length - 1 ? 1 : 0}
              borderColor={GlobalStyles.colors.tableBorder}
            >
              <Box
                flex={1}
                py={3}
                px={2}
                borderRightWidth={1}
                borderColor={GlobalStyles.colors.tableBorder}
                justifyContent="center"
              >
                <Text
                  fontFamily={GlobalStyles.text.fontFamily}
                  fontSize="12px"
                  textAlign="center"
                  numberOfLines={2}
                >
                  {getInvoiceServiceName(inv)}
                </Text>
              </Box>
              <Box
                flex={1}
                py={3}
                px={2}
                borderRightWidth={1}
                borderColor={GlobalStyles.colors.tableBorder}
                justifyContent="center"
              >
                <Text
                  fontFamily={GlobalStyles.text.fontFamily}
                  fontSize="12px"
                  textAlign="center"
                >
                  {inv.amount} USD
                </Text>
              </Box>
              <Box
                flex={1}
                py={3}
                px={2}
                borderRightWidth={1}
                borderColor={GlobalStyles.colors.tableBorder}
                justifyContent="center"
              >
                <Text
                  fontFamily={GlobalStyles.text.fontFamily}
                  fontSize="12px"
                  textAlign="center"
                >
                  {formatDate(inv.created_at || inv.createdAt)}
                </Text>
              </Box>
              <Box
                flex={1}
                py={3}
                px={2}
                borderRightWidth={1}
                borderColor={GlobalStyles.colors.tableBorder}
                justifyContent="center"
                alignItems="center"
              >
                <Pressable onPress={() => fetchInvoiceDetails(inv.id)}>
                  <Text
                    color={GlobalStyles.colors.linkBlue}
                    fontFamily={GlobalStyles.text.fontFamily}
                    fontSize="12px"
                  >
                    View
                  </Text>
                </Pressable>
              </Box>
              <Box
                flex={1}
                py={3}
                px={2}
                justifyContent="center"
                alignItems="center"
              >
                <Text
                  color={
                    inv.status === "paid"
                      ? GlobalStyles.colors.green
                      : GlobalStyles.colors.red
                  }
                  fontFamily={GlobalStyles.text.fontFamily}
                  fontSize="12px"
                  textTransform="capitalize"
                >
                  {inv.status || "—"}
                </Text>
              </Box>
            </HStack>
          ))}
        </Box>
      )}

      <Modal
        isOpen={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
      >
        <Modal.Content maxWidth="380px">
          <Modal.CloseButton />
          <Modal.Header>Invoice Receipt</Modal.Header>
          <Modal.Body>
            {selectedInvoice && (
              <VStack space={2}>
                {[
                  ["Invoice ID", selectedInvoice.id],
                  ["Subscription ID", selectedInvoice.subscription_id],
                  ["Amount", `$${selectedInvoice.amount} USD`],
                  ["Status", selectedInvoice.status],
                  [
                    "Date",
                    formatDate(
                      selectedInvoice.created_at || selectedInvoice.createdAt,
                    ),
                  ],
                  selectedInvoice.user
                    ? ["Email", selectedInvoice.user.email]
                    : null,
                ]
                  .filter(Boolean)
                  .map(([label, value]) => (
                    <HStack key={label} justifyContent="space-between">
                      <Text
                        fontFamily={GlobalStyles.text.fontFamily}
                        fontWeight="600"
                        fontSize="13px"
                      >
                        {label}:
                      </Text>
                      <Text
                        fontFamily={GlobalStyles.text.fontFamily}
                        fontSize="13px"
                        color={
                          label === "Status"
                            ? value === "paid"
                              ? GlobalStyles.colors.green
                              : GlobalStyles.colors.red
                            : undefined
                        }
                        textTransform={
                          label === "Status" ? "capitalize" : undefined
                        }
                      >
                        {value}
                      </Text>
                    </HStack>
                  ))}
              </VStack>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="ghost" onPress={() => setShowInvoiceModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal>
    </>
  );
};

const AlertsTable = ({ userId }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        setIsLoading(true);
        const url = userId
          ? `${API_BASE_URL}/subscriptions/user/${userId}`
          : `${API_BASE_URL}/subscriptions`;
        const r = await axios.get(url);
        const data = Array.isArray(r.data) ? r.data : [];
        setSubscriptions(
          data.filter((sub) => {
            const daysLeft = Math.ceil(
              (new Date(sub.end_date) - new Date()) / 86400000,
            );
            return daysLeft <= 60 || sub.status === "EXPIRED";
          }),
        );
      } catch (e) {
        if (e.response?.status !== 404)
          showToast(toast, "Failed to load alerts.", true);
        setSubscriptions([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAlerts();
  }, [userId]);

  const getExpiryText = (sub) => {
    if (sub.status === "EXPIRED") return "Expired";
    const daysLeft = Math.ceil(
      (new Date(sub.end_date) - new Date()) / 86400000,
    );
    const m = Math.floor(daysLeft / 30);
    return m > 0
      ? `${m} Month${m > 1 ? "s" : ""} Left`
      : `${daysLeft} Day${daysLeft > 1 ? "s" : ""} Left`;
  };

  if (isLoading)
    return (
      <Box py={10} alignItems="center">
        <Spinner size="lg" color={GlobalStyles.colors.blue} />
        <Text mt={2} fontFamily={GlobalStyles.text.fontFamily}>
          Loading...
        </Text>
      </Box>
    );
  if (!subscriptions.length)
    return (
      <Box borderWidth={1} borderColor={GlobalStyles.colors.tableBorder} p={4}>
        <Text textAlign="center" fontFamily={GlobalStyles.text.fontFamily}>
          No alerts at this time
        </Text>
      </Box>
    );

  return (
    <Box borderWidth={1} borderColor={GlobalStyles.colors.tableBorder} w="100%">
      <HStack
        borderBottomWidth={1}
        borderColor={GlobalStyles.colors.tableBorder}
      >
        {["Service Name", "Expiry", "Inbox"].map((h, i) => (
          <Box
            key={h}
            flex={1}
            py={3}
            px={2}
            borderRightWidth={i < 2 ? 1 : 0}
            borderColor={GlobalStyles.colors.tableBorder}
          >
            <Text
              fontWeight="600"
              fontFamily={GlobalStyles.text.fontFamily}
              fontSize="13px"
              textAlign="center"
            >
              {h}
            </Text>
          </Box>
        ))}
      </HStack>
      {subscriptions.map((sub, idx) => (
        <HStack
          key={sub.id}
          borderBottomWidth={idx < subscriptions.length - 1 ? 1 : 0}
          borderColor={GlobalStyles.colors.tableBorder}
        >
          <Box
            flex={1}
            py={3}
            px={2}
            borderRightWidth={1}
            borderColor={GlobalStyles.colors.tableBorder}
            justifyContent="center"
          >
            <Text
              fontFamily={GlobalStyles.text.fontFamily}
              fontSize="13px"
              textAlign="center"
            >
              {sub.service
                ? SERVICE_LABELS[sub.service] || sub.service
                : sub.Plan?.name || `Plan ${sub.plan_id}`}
            </Text>
          </Box>
          <Box
            flex={1}
            py={3}
            px={2}
            borderRightWidth={1}
            borderColor={GlobalStyles.colors.tableBorder}
            justifyContent="center"
          >
            <Text
              fontFamily={GlobalStyles.text.fontFamily}
              fontSize="13px"
              textAlign="center"
              numberOfLines={2}
            >
              {getExpiryText(sub)}
            </Text>
          </Box>
          <Box
            flex={1}
            py={3}
            px={2}
            justifyContent="center"
            alignItems="center"
          >
            <Pressable>
              <Text
                color={GlobalStyles.colors.linkBlue}
                fontFamily={GlobalStyles.text.fontFamily}
                fontSize="13px"
              >
                Message
              </Text>
            </Pressable>
          </Box>
        </HStack>
      ))}
    </Box>
  );
};

const AvailableServicesTable = ({ onPayClick, onTrialClick }) => {
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const toast = useToast();
  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/plans`)
      .then((r) =>
        setServices(
          r.data.map((p) => ({
            ...p,
            charges: {
              annual: `${(parseFloat(p.price) * 12).toFixed(0)} USD`,
              monthly: `${p.price} USD`,
            },
            trial: `${p.duration} days`,
          })),
        ),
      )
      .catch(() => showToast(toast, "Failed to load services.", true))
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading)
    return (
      <Box py={10} alignItems="center">
        <Spinner size="lg" color={GlobalStyles.colors.blue} />
      </Box>
    );
  if (!services.length)
    return (
      <Box borderWidth={1} borderColor={GlobalStyles.colors.tableBorder} p={4}>
        <Text textAlign="center">No services available</Text>
      </Box>
    );

  return (
    <Box borderWidth={1} borderColor={GlobalStyles.colors.tableBorder} w="100%">
      <HStack
        borderBottomWidth={1}
        borderColor={GlobalStyles.colors.tableBorder}
      >
        {["Service Name", "Charges", "Trial", "Subscribe", ""].map((h, i) => (
          <Box
            key={i}
            flex={1}
            py={3}
            px={2}
            borderRightWidth={i < 4 ? 1 : 0}
            borderColor={GlobalStyles.colors.tableBorder}
            justifyContent="center"
            alignItems="center"
          >
            <Text
              fontWeight="600"
              fontFamily={GlobalStyles.text.fontFamily}
              fontSize="13px"
            >
              {h}
            </Text>
          </Box>
        ))}
      </HStack>
      {services.map((s, idx) => (
        <HStack
          key={idx}
          borderBottomWidth={idx < services.length - 1 ? 1 : 0}
          borderColor={GlobalStyles.colors.tableBorder}
        >
          <Box
            flex={1}
            py={3}
            px={2}
            borderRightWidth={1}
            borderColor={GlobalStyles.colors.tableBorder}
            justifyContent="center"
            alignItems="center"
          >
            <Text fontFamily={GlobalStyles.text.fontFamily} fontSize="13px">
              {s.name}
            </Text>
          </Box>
          <Box
            flex={1}
            py={3}
            px={2}
            borderRightWidth={1}
            borderColor={GlobalStyles.colors.tableBorder}
            justifyContent="center"
            alignItems="center"
          >
            <Text fontFamily={GlobalStyles.text.fontFamily} fontSize="12px">
              {s.charges.annual} / yr
            </Text>
            <Text fontFamily={GlobalStyles.text.fontFamily} fontSize="12px">
              {s.charges.monthly} / mo
            </Text>
          </Box>
          <Box
            flex={1}
            py={3}
            px={2}
            borderRightWidth={1}
            borderColor={GlobalStyles.colors.tableBorder}
            justifyContent="center"
            alignItems="center"
          >
            <Pressable onPress={() => onTrialClick(s)}>
              <Text
                color={GlobalStyles.colors.linkBlue}
                fontFamily={GlobalStyles.text.fontFamily}
                fontSize="12px"
              >
                Start Trial
              </Text>
              <Text
                fontFamily={GlobalStyles.text.fontFamily}
                fontSize="11px"
                textAlign="center"
              >
                ({s.trial})
              </Text>
            </Pressable>
          </Box>
          <Box
            flex={1}
            py={3}
            px={2}
            borderRightWidth={1}
            borderColor={GlobalStyles.colors.tableBorder}
            justifyContent="center"
            alignItems="center"
          >
            <Pressable onPress={() => onPayClick(s)}>
              <Text
                color={GlobalStyles.colors.linkBlue}
                fontFamily={GlobalStyles.text.fontFamily}
                fontSize="13px"
              >
                Pay
              </Text>
            </Pressable>
          </Box>
          <Box
            flex={1}
            py={3}
            px={2}
            justifyContent="center"
            alignItems="center"
          >
            <Pressable>
              <Text
                color={GlobalStyles.colors.linkBlue}
                fontFamily={GlobalStyles.text.fontFamily}
                fontSize="13px"
              >
                Learn More
              </Text>
            </Pressable>
          </Box>
        </HStack>
      ))}
    </Box>
  );
};

const CancelConfirmView = ({ service, onClose, onCancelSuccess }) => {
  const [isCancelling, setIsCancelling] = useState(false);
  const toast = useToast();
  const doCancel = async () => {
    if (!service?.id) {
      showToast(toast, "Subscription ID not found.", true);
      return;
    }
    try {
      setIsCancelling(true);
      await axios.put(`${API_BASE_URL}/subscriptions/${service.id}`, {
        status: "CANCELLED",
      });
      showToast(toast, "Subscription cancelled.");
      onCancelSuccess && onCancelSuccess();
    } catch {
      showToast(toast, "Failed to cancel subscription.", true);
    } finally {
      setIsCancelling(false);
    }
  };
  return (
    <Box borderWidth={1} borderColor={GlobalStyles.colors.black} w="100%" p={6}>
      <Pressable onPress={onClose} mb={4} alignSelf="flex-start">
        <Text
          color={GlobalStyles.colors.blue}
          fontSize="13px"
          fontFamily={GlobalStyles.text.fontFamily}
        >
          ← Back
        </Text>
      </Pressable>
      <Text
        fontSize="16px"
        fontWeight="400"
        fontFamily={GlobalStyles.text.fontFamily}
        textAlign="center"
        mb={4}
      >
        Cancel {service?.name}
      </Text>
      <Box
        borderWidth={1}
        borderColor={GlobalStyles.colors.black}
        borderRadius={10}
        p={5}
        alignItems="center"
      >
        <Text
          textAlign="center"
          fontSize="14px"
          mb={5}
          fontFamily={GlobalStyles.text.fontFamily}
        >
          Are you sure you want to cancel{"\n"}
          {service?.name}?
        </Text>
        <HStack space={4}>
          <Button
            bg={GlobalStyles.colors.blue}
            px={8}
            py={2.5}
            borderRadius={8}
            onPress={doCancel}
            isDisabled={isCancelling}
            _text={{ color: "white", fontFamily: GlobalStyles.text.fontFamily }}
          >
            {isCancelling ? <Spinner color="white" size="sm" /> : "Yes"}
          </Button>
          <Button
            variant="outline"
            borderColor={GlobalStyles.colors.blue}
            px={8}
            py={2.5}
            borderRadius={8}
            onPress={onClose}
            _text={{
              color: GlobalStyles.colors.blue,
              fontFamily: GlobalStyles.text.fontFamily,
            }}
          >
            No
          </Button>
        </HStack>
      </Box>
    </Box>
  );
};

const CurrentServicesTable = ({ userId }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [cancelConfirmService, setCancelConfirmService] = useState(null);
  const toast = useToast();

  useEffect(() => {
    fetchUserSubscriptions();
  }, [userId]);

  const fetchUserSubscriptions = async () => {
    try {
      setIsLoading(true);
      const url = userId
        ? `${API_BASE_URL}/subscriptions/user/${userId}`
        : `${API_BASE_URL}/subscriptions`;
      const r = await axios.get(url);
      setSubscriptions(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      if (e.response?.status !== 404)
        showToast(toast, "Failed to load services.", true);
      setSubscriptions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setDeletingId(id);
      await axios.delete(`${API_BASE_URL}/subscriptions/${id}`);
      showToast(toast, "Subscription deleted.");
      setSubscriptions((prev) => prev.filter((s) => s.id !== id));
    } catch {
      showToast(toast, "Failed to delete subscription.", true);
    } finally {
      setDeletingId(null);
    }
  };

  const getTimeLeft = (sub) => {
    if (sub.status === "CANCELLED") return "Cancelled";
    if (sub.status === "EXPIRED") return "Expired";
    if (sub.status === "INACTIVE") return "Inactive";
    const daysLeft = Math.ceil(
      (new Date(sub.end_date) - new Date()) / 86400000,
    );
    const m = Math.floor(daysLeft / 30);
    return m > 0 ? `${m} Mo Left` : `${daysLeft}d Left`;
  };

  const getStatusColor = (s) =>
    ({
      ACTIVE: GlobalStyles.colors.green,
      CANCELLED: GlobalStyles.colors.red,
      EXPIRED: GlobalStyles.colors.red,
    })[s] || GlobalStyles.colors.black;

  const getServiceName = (sub) => {
    if (sub.service && SERVICE_LABELS[sub.service])
      return SERVICE_LABELS[sub.service];
    if (sub.service)
      return sub.service.charAt(0).toUpperCase() + sub.service.slice(1);
    return sub.Plan?.name || sub.plan?.name || `Plan ${sub.plan_id}`;
  };

  if (cancelConfirmService) {
    return (
      <CancelConfirmView
        service={cancelConfirmService}
        onClose={() => setCancelConfirmService(null)}
        onCancelSuccess={() => {
          setSubscriptions((prev) =>
            prev.map((s) =>
              s.id === cancelConfirmService.id
                ? { ...s, status: "CANCELLED" }
                : s,
            ),
          );
          setCancelConfirmService(null);
        }}
      />
    );
  }

  if (isLoading)
    return (
      <Box py={10} alignItems="center">
        <Spinner size="lg" color={GlobalStyles.colors.blue} />
        <Text mt={2} fontFamily={GlobalStyles.text.fontFamily}>
          Loading...
        </Text>
      </Box>
    );
  if (!subscriptions.length)
    return (
      <Box borderWidth={1} borderColor={GlobalStyles.colors.tableBorder} p={4}>
        <Text textAlign="center" fontFamily={GlobalStyles.text.fontFamily}>
          No subscriptions found
        </Text>
      </Box>
    );

  return (
    <Box borderWidth={1} borderColor={GlobalStyles.colors.tableBorder} w="100%">
      <HStack
        borderBottomWidth={1}
        borderColor={GlobalStyles.colors.tableBorder}
      >
        {[
          { label: "Service Name", flex: 1.3 },
          { label: "Time Left", flex: 0.9 },
          { label: "Status", flex: 0.9 },
          { label: "Action", flex: 0.9 },
        ].map((col, i) => (
          <Box
            key={i}
            flex={col.flex}
            py={3}
            px={2}
            borderRightWidth={i < 3 ? 1 : 0}
            borderColor={GlobalStyles.colors.tableBorder}
            justifyContent="center"
          >
            <Text
              fontWeight="600"
              fontFamily={GlobalStyles.text.fontFamily}
              fontSize="13px"
              textAlign="center"
            >
              {col.label}
            </Text>
          </Box>
        ))}
      </HStack>
      {subscriptions.map((sub, idx) => {
        const isCancelled = sub.status === "CANCELLED";
        const isExpired = sub.status === "EXPIRED";
        const isActive = sub.status === "ACTIVE";
        const isDeleting = deletingId === sub.id;
        const displayName = getServiceName(sub);

        return (
          <HStack
            key={sub.id}
            borderBottomWidth={idx < subscriptions.length - 1 ? 1 : 0}
            borderColor={GlobalStyles.colors.tableBorder}
          >
            <Box
              flex={1.3}
              py={3}
              px={2}
              borderRightWidth={1}
              borderColor={GlobalStyles.colors.tableBorder}
              justifyContent="center"
            >
              <Text
                color={
                  isCancelled
                    ? GlobalStyles.colors.red
                    : GlobalStyles.colors.black
                }
                fontFamily={GlobalStyles.text.fontFamily}
                fontSize="13px"
                textAlign="center"
                numberOfLines={2}
              >
                {displayName}
              </Text>
            </Box>
            <Box
              flex={0.9}
              py={3}
              px={2}
              borderRightWidth={1}
              borderColor={GlobalStyles.colors.tableBorder}
              justifyContent="center"
            >
              <Text
                fontFamily={GlobalStyles.text.fontFamily}
                fontSize="12px"
                textAlign="center"
                numberOfLines={2}
              >
                {getTimeLeft(sub)}
              </Text>
            </Box>
            <Box
              flex={0.9}
              py={3}
              px={2}
              borderRightWidth={1}
              borderColor={GlobalStyles.colors.tableBorder}
              justifyContent="center"
              alignItems="center"
            >
              <Box
                bg={isCancelled || isExpired ? "#FFE4E4" : "#E4FFE9"}
                borderRadius={5}
                px={2}
                py={0.5}
              >
                <Text
                  color={getStatusColor(sub.status)}
                  fontFamily={GlobalStyles.text.fontFamily}
                  fontSize="11px"
                  fontWeight="600"
                  textTransform="capitalize"
                >
                  {sub.status}
                </Text>
              </Box>
            </Box>
            <Box
              flex={0.9}
              py={3}
              px={2}
              justifyContent="center"
              alignItems="center"
            >
              {isActive && (
                <Pressable
                  onPress={() =>
                    setCancelConfirmService({ ...sub, name: displayName })
                  }
                >
                  <Text
                    color={GlobalStyles.colors.red}
                    fontFamily={GlobalStyles.text.fontFamily}
                    fontSize="12px"
                    fontWeight="600"
                  >
                    Cancel
                  </Text>
                </Pressable>
              )}
              {(isCancelled || isExpired) &&
                (isDeleting ? (
                  <Spinner size="sm" color={GlobalStyles.colors.red} />
                ) : (
                  <Pressable onPress={() => handleDelete(sub.id)}>
                    <Text
                      color={GlobalStyles.colors.red}
                      fontFamily={GlobalStyles.text.fontFamily}
                      fontSize="12px"
                      fontWeight="600"
                    >
                      Delete
                    </Text>
                  </Pressable>
                ))}
              {!isActive && !isCancelled && !isExpired && (
                <Text color="gray.400" fontSize="13px">
                  —
                </Text>
              )}
            </Box>
          </HStack>
        );
      })}
    </Box>
  );
};

const PaymentForm = ({
  service,
  onConfirm,
  onBack,
  preselectedPlan,
  loggedInUser,
}) => {
  const [subscriptionPeriod, setSubscriptionPeriod] = useState("months");
  const [monthsValue, setMonthsValue] = useState("");
  const [yearValue, setYearValue] = useState("");
  const [paymentMode, setPaymentMode] = useState("monthly");
  const [selectedService, setSelectedService] = useState(
    service?.service || "",
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({
    cardType: "",
    cardHolderName: "",
    cardNumber: "",
    expiry: "",
    code: "",
  });
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [selectedFixedPlan, setSelectedFixedPlan] = useState(
    preselectedPlan || null,
  );
  const [existingSubscriptions, setExistingSubscriptions] = useState([]);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const toast = useToast();
  const userId = loggedInUser?.id || loggedInUser?.user_id || null;
  const userEmail = loggedInUser?.email || "";

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/plans`)
      .then((r) => setPlans(r.data))
      .catch(() => setPlans([]))
      .finally(() => setPlansLoading(false));
    fetchUserSubscriptions();
  }, []);

  useEffect(() => {
    if (preselectedPlan) setSelectedFixedPlan(preselectedPlan);
  }, [preselectedPlan]);

  const fetchUserSubscriptions = async () => {
    if (!userId) return;
    try {
      const res = await axios.get(
        `${API_BASE_URL}/subscriptions/user/${userId}`,
      );
      const data = Array.isArray(res.data) ? res.data : [];
      setExistingSubscriptions(data.filter((s) => s.status === "ACTIVE"));
    } catch {
      setExistingSubscriptions([]);
    }
  };

  const hasActiveSubscriptionForService = (svc) => {
    if (!svc) return false;
    return existingSubscriptions.some(
      (s) => s.service === svc && s.status === "ACTIVE",
    );
  };

  const handleSelectPlan = (plan) => {
    setSelectedFixedPlan(plan);
    if (plan) {
      setPaymentMode(plan.duration <= 30 ? "monthly" : "annual");
      setMonthsValue("");
      setYearValue("");
    }
  };

  const getOneMonthPrice = () => {
    if (plans.length > 0)
      return parseFloat(
        [...plans].sort((a, b) => a.duration - b.duration)[0].price,
      );
    return parseFloat(service?.price || 0);
  };

  const calculateTotal = () => {
    if (selectedFixedPlan)
      return parseFloat(selectedFixedPlan.price).toFixed(2);
    const p = getOneMonthPrice();
    if (!p) return "0.00";
    const m =
      subscriptionPeriod === "months"
        ? parseInt(monthsValue || 0)
        : parseInt(yearValue || 0) * 12;
    return !m || m <= 0 ? "0.00" : (p * m).toFixed(2);
  };

  const handleConfirmClick = () => {
    if (!userId) {
      showToast(toast, "User not found. Please log in again.", true);
      return;
    }
    const svcToCheck = selectedService || service?.service;
    if (svcToCheck && hasActiveSubscriptionForService(svcToCheck)) {
      setShowDuplicateWarning(true);
      return;
    }
    proceedWithPayment();
  };

  const proceedWithPayment = async () => {
    setShowDuplicateWarning(false);
    try {
      setIsProcessing(true);
      const startDate = new Date();
      const months = selectedFixedPlan
        ? Math.max(1, Math.round(selectedFixedPlan.duration / 30))
        : subscriptionPeriod === "months"
          ? parseInt(monthsValue || 1)
          : parseInt(yearValue || 1) * 12;
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + months);
      await axios.post(`${API_BASE_URL}/subscriptions`, {
        user_id: userId,
        plan_id: selectedFixedPlan?.id || service?.id || 1,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        status: "ACTIVE",
        ...(selectedService ? { service: selectedService } : {}),
      });
      showToast(toast, "Subscription created successfully!");
      setIsProcessing(false);
      onConfirm();
    } catch {
      showToast(
        toast,
        "Failed to create subscription. Please try again.",
        true,
      );
      setIsProcessing(false);
    }
  };

  const currentServiceLabel =
    SERVICE_LABELS[selectedService || service?.service] ||
    selectedService ||
    service?.service ||
    "";

  return (
    <Box p={[4, 5, 6]}>
      <VStack space={3}>
        <Pressable onPress={onBack}>
          <Text
            color={GlobalStyles.colors.blue}
            fontSize="13px"
            fontFamily={GlobalStyles.text.fontFamily}
          >
            ← Back
          </Text>
        </Pressable>

        <Box
          borderWidth={1}
          borderColor={GlobalStyles.colors.mustard}
          borderRadius={8}
          p={3}
          bg="#FFFBF0"
        >
          <Text
            fontFamily={GlobalStyles.text.fontFamily}
            fontSize="12px"
            color={GlobalStyles.colors.blue}
            lineHeight="18px"
          >
            {plansLoading
              ? "Loading plans..."
              : plans.length === 0
                ? "1 Month: 35 USD  —  6 Months: 200 USD  —  1 Year: 350 USD"
                : [...plans]
                    .sort((a, b) => a.duration - b.duration)
                    .map(
                      (p) =>
                        `${durationToLabel(p.duration)}: ${parseFloat(p.price).toFixed(0)} USD`,
                    )
                    .join("  —  ")}
          </Text>
        </Box>

        <VStack space={1}>
          <Text
            fontSize="13px"
            fontWeight="600"
            fontFamily={GlobalStyles.text.fontFamily}
            color={GlobalStyles.colors.blue}
          >
            Select a Plan
          </Text>
          <PlanCards
            plans={plans}
            isLoading={plansLoading}
            selectedPlanId={selectedFixedPlan?.id}
            onSelectPlan={handleSelectPlan}
          />
        </VStack>

        {!service && (
          <VStack space={1}>
            <Text
              fontSize="13px"
              fontWeight="600"
              fontFamily={GlobalStyles.text.fontFamily}
              color={GlobalStyles.colors.blue}
            >
              Service (optional)
            </Text>
            <Select
              selectedValue={selectedService}
              onValueChange={setSelectedService}
              placeholder="Choose a service"
              borderColor="#000"
              borderWidth={1}
              borderRadius={0}
              fontSize="13px"
              fontFamily={GlobalStyles.text.fontFamily}
              h="40px"
              w={["100%", "200px"]}
              _actionSheetContent={{ maxH: "220px" }}
            >
              {SERVICE_OPTIONS.map((s) => (
                <Select.Item key={s} label={SERVICE_LABELS[s]} value={s} />
              ))}
            </Select>
            {selectedService &&
              hasActiveSubscriptionForService(selectedService) && (
                <Box
                  bg="#FFF3CD"
                  borderColor="#FFC107"
                  borderWidth={1}
                  borderRadius={6}
                  px={3}
                  py={2}
                  mt={1}
                >
                  <Text
                    fontSize="11px"
                    color="#856404"
                    fontFamily={GlobalStyles.text.fontFamily}
                  >
                    ⚠️ You already have an active subscription for{" "}
                    <Text fontWeight="700">
                      {SERVICE_LABELS[selectedService] || selectedService}
                    </Text>
                    . You can still subscribe again.
                  </Text>
                </Box>
              )}
          </VStack>
        )}

        <HStack
          space={[0, 0, 8]}
          flexDirection={["column", "column", "row"]}
          alignItems="flex-start"
        >
          <VStack space={2} w={["100%", "100%", "auto"]}>
            <Text
              fontSize="13px"
              fontWeight="400"
              fontFamily={GlobalStyles.text.fontFamily}
            >
              Subscription Period
            </Text>
            <Radio.Group
              value={subscriptionPeriod}
              onChange={(v) => {
                setSubscriptionPeriod(v);
                setSelectedFixedPlan(null);
              }}
              name="sp"
            >
              <HStack space={3} alignItems="center">
                <Radio
                  value="months"
                  size="sm"
                  _checked={{
                    bg: GlobalStyles.colors.blue,
                    borderColor: GlobalStyles.colors.blue,
                  }}
                  borderColor="#000"
                >
                  <HStack space={2} alignItems="center" ml={1}>
                    <Input
                      w="42px"
                      h="40px"
                      value={monthsValue}
                      onChangeText={(v) => {
                        setMonthsValue(v);
                        setSelectedFixedPlan(null);
                        setSubscriptionPeriod("months");
                      }}
                      keyboardType="numeric"
                      fontFamily={GlobalStyles.text.fontFamily}
                      fontSize="13px"
                      borderColor="#000"
                      borderWidth={1}
                      textAlign="center"
                      bg="white"
                      borderRadius={0}
                    />
                    <Text
                      fontSize="13px"
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      Months
                    </Text>
                  </HStack>
                </Radio>
                <Radio
                  value="year"
                  size="sm"
                  _checked={{
                    bg: GlobalStyles.colors.blue,
                    borderColor: GlobalStyles.colors.blue,
                  }}
                  borderColor="#000"
                >
                  <HStack space={2} alignItems="center" ml={1}>
                    <Input
                      w="42px"
                      h="40px"
                      value={yearValue}
                      onChangeText={(v) => {
                        setYearValue(v);
                        setSelectedFixedPlan(null);
                        setSubscriptionPeriod("year");
                      }}
                      keyboardType="numeric"
                      fontFamily={GlobalStyles.text.fontFamily}
                      fontSize="13px"
                      borderColor="#000"
                      borderWidth={1}
                      textAlign="center"
                      bg="white"
                      borderRadius={0}
                    />
                    <Text
                      fontSize="13px"
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      Year
                    </Text>
                  </HStack>
                </Radio>
              </HStack>
            </Radio.Group>

            <Text
              fontSize="13px"
              fontFamily={GlobalStyles.text.fontFamily}
              textAlign="center"
              mt={1}
            >
              Payment Mode
            </Text>
            <Radio.Group
              value={paymentMode}
              onChange={(v) => {
                setPaymentMode(v);
                setSelectedFixedPlan(null);
              }}
              name="pm"
            >
              <HStack space={5} justifyContent="center">
                {["monthly", "annual"].map((m) => (
                  <Radio
                    key={m}
                    value={m}
                    size="sm"
                    _checked={{
                      bg: GlobalStyles.colors.blue,
                      borderColor: GlobalStyles.colors.blue,
                    }}
                    borderColor="#000"
                  >
                    <Text
                      fontSize="13px"
                      fontFamily={GlobalStyles.text.fontFamily}
                      ml={1}
                      textTransform="capitalize"
                    >
                      {m}
                    </Text>
                  </Radio>
                ))}
              </HStack>
            </Radio.Group>

            <VStack space={1} mt={1}>
              {[
                { key: "cardType", placeholder: "Card type" },
                { key: "cardHolderName", placeholder: "Card holder name" },
                {
                  key: "cardNumber",
                  placeholder: "Card number",
                  numeric: true,
                  max: 16,
                },
                { key: "expiry", placeholder: "Expiry (MM/YY)", max: 5 },
                {
                  key: "code",
                  placeholder: "CVV",
                  numeric: true,
                  max: 3,
                  secure: true,
                },
              ].map(({ key, placeholder, numeric, max, secure }) => (
                <Input
                  key={key}
                  placeholder={placeholder}
                  value={formData[key]}
                  onChangeText={(v) => setFormData({ ...formData, [key]: v })}
                  keyboardType={numeric ? "numeric" : "default"}
                  maxLength={max}
                  secureTextEntry={secure}
                  w={["100%", "210px"]}
                  h="42px"
                  fontSize="13px"
                  fontFamily={GlobalStyles.text.fontFamily}
                  borderColor="#000"
                  borderWidth={1}
                  bg="white"
                  textAlign="center"
                  borderRadius={0}
                />
              ))}
            </VStack>
          </VStack>

          <VStack
            space={1}
            w={["100%", "100%", "auto"]}
            mt={[5, 5, 0]}
            alignItems="center"
            pl={[0, 0, 6]}
            borderLeftWidth={[0, 0, 1]}
            borderLeftColor={["transparent", "transparent", "#000"]}
          >
            <Text
              fontSize="16px"
              fontWeight="700"
              fontFamily={GlobalStyles.text.fontFamily}
            >
              Order Summary
            </Text>
            <VStack space={1} alignItems="center">
              <Text
                fontSize="14px"
                fontFamily={GlobalStyles.text.fontFamily}
                textAlign="center"
              >
                {selectedFixedPlan
                  ? durationToLabel(selectedFixedPlan.duration)
                  : service?.name || "Service"}
              </Text>
              <Text
                fontSize="14px"
                fontFamily={GlobalStyles.text.fontFamily}
                textAlign="center"
                textTransform="capitalize"
              >
                {paymentMode}
              </Text>
              {selectedFixedPlan && (
                <Text
                  fontSize="14px"
                  fontFamily={GlobalStyles.text.fontFamily}
                  textAlign="center"
                >
                  {parseFloat(selectedFixedPlan.price).toFixed(0)} USD
                </Text>
              )}
              {userEmail ? (
                <Text
                  fontSize="11px"
                  color="gray.500"
                  fontFamily={GlobalStyles.text.fontFamily}
                  textAlign="center"
                >
                  {userEmail}
                </Text>
              ) : null}
              <Text
                fontSize="16px"
                fontWeight="700"
                fontFamily={GlobalStyles.text.fontFamily}
                mt={1}
              >
                Total: {calculateTotal()} USD
              </Text>
            </VStack>
            <Button
              variant="outline"
              borderColor={GlobalStyles.colors.blue}
              borderWidth={2}
              onPress={handleConfirmClick}
              w={["100%", "180px"]}
              h="44px"
              mt={2}
              borderRadius={0}
              bg="white"
              isDisabled={isProcessing}
              _text={{
                color: GlobalStyles.colors.blue,
                fontFamily: GlobalStyles.text.fontFamily,
                fontSize: "14px",
                fontWeight: "600",
              }}
              _pressed={{
                bg: GlobalStyles.colors.blue,
                _text: { color: "white" },
              }}
            >
              {isProcessing ? (
                <Spinner color={GlobalStyles.colors.blue} />
              ) : (
                "Confirm"
              )}
            </Button>
          </VStack>
        </HStack>
      </VStack>

      <AlreadySubscribedModal
        isOpen={showDuplicateWarning}
        onClose={() => setShowDuplicateWarning(false)}
        serviceName={currentServiceLabel}
        onProceed={proceedWithPayment}
      />
    </Box>
  );
};

const Subscription = ({ onBack }) => {
  const user = useSelector((state) => state.user.user);
  const userId = user?.id || user?.user_id || null;
  const [activeView, setActiveView] = useState("current");
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [preselectedPlan, setPreselectedPlan] = useState(null);

  const headerButtons = [
    {
      text: "Current\nServices",
      view: "current",
      icon: require("../../../assets/current_services.png"),
      disabled: false,
    },
    {
      text: "Available\nServices",
      view: "available",
      icon: require("../../../assets/available_services.png"),
      disabled: true,
    },
    {
      text: "Account &\nPayments",
      view: "account",
      icon: require("../../../assets/accountPayments.png"),
      disabled: false,
    },
    {
      text: "Alerts",
      view: "alerts",
      icon: require("../../../assets/alerts.png"),
      disabled: true,
    },
  ];

  const sidebarItems = [
    {
      text: "Subscription\nManagement",
      active: true,
      icon: require("../../../assets/subscription_management.png"),
      disabled: true,
    },
    {
      text: "Service\nDashboard",
      active: false,
      icon: require("../../../assets/service_dashboard.png"),
      disabled: true,
    },
    {
      text: "Contact Us",
      active: false,
      icon: require("../../../assets/contact_us.png"),
      disabled: true,
    },
  ];

  return (
    <ScrollView bg={GlobalStyles.colors.lightGrey} flex={1}>
      <Center>
        <Stack mt={4} w={[350, 450, 750]} alignItems="center">
          {onBack && (
            <HStack w="100%" mb={3} px={4}>
              <Pressable onPress={onBack}>
                <Text
                  color={GlobalStyles.colors.blue}
                  fontWeight="500"
                  fontSize={14}
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  ← Back to Services
                </Text>
              </Pressable>
            </HStack>
          )}

          <HStack
            space={3}
            alignItems="flex-start"
            w="100%"
            flexDirection={["column", "column", "row"]}
          >
            <Box
              w={["100%", "100%", "130px"]}
              flexShrink={0}
              mt={["0", "0", "20px"]}
            >
              <VStack space={2} display={["none", "none", "flex"]}>
                {sidebarItems.map((item, idx) => (
                  <Pressable
                    key={idx}
                    disabled={item.disabled}
                    onPress={item.disabled ? undefined : () => {}}
                    borderWidth={2}
                    borderColor={
                      item.active
                        ? GlobalStyles.colors.mustard
                        : GlobalStyles.colors.blue
                    }
                    bg="white"
                    borderRadius={12}
                    px={3}
                    py={2.5}
                    opacity={item.disabled ? 0.45 : 1}
                  >
                    <HStack space={2} alignItems="center">
                      <Box
                        bg={GlobalStyles.colors.blue}
                        borderRadius={6}
                        w="20px"
                        h="20px"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Image
                          source={item.icon}
                          style={{ width: 12, height: 12 }}
                          alt={item.text}
                          tintColor="white"
                        />
                      </Box>
                      <Text
                        fontSize="10px"
                        color={GlobalStyles.colors.blue}
                        fontFamily={GlobalStyles.text.fontFamily}
                        numberOfLines={2}
                        flex={1}
                      >
                        {item.text}
                      </Text>
                    </HStack>
                  </Pressable>
                ))}
              </VStack>
            </Box>

            <Box flex={1} w={["100%", "100%", "auto"]}>
              <Box px={["4", "4", "0"]}>
                <HStack space={2} mb={3} flexWrap="wrap">
                  {headerButtons.map((btn) => (
                    <Button
                      key={btn.view}
                      isDisabled={btn.disabled}
                      bg={
                        activeView === btn.view
                          ? GlobalStyles.colors.mustard
                          : GlobalStyles.colors.blue
                      }
                      onPress={() => {
                        if (btn.disabled) return;
                        setActiveView(btn.view);
                        setShowPaymentForm(false);
                      }}
                      borderRadius={8}
                      px={4}
                      py={2.5}
                      opacity={btn.disabled ? 0.45 : 1}
                      _pressed={{
                        bg: btn.disabled
                          ? activeView === btn.view
                            ? GlobalStyles.colors.mustard
                            : GlobalStyles.colors.blue
                          : activeView === btn.view
                            ? GlobalStyles.colors.mustard
                            : "#001544",
                      }}
                    >
                      <HStack space={2} alignItems="center">
                        <Image
                          source={btn.icon}
                          style={{ width: 14, height: 14 }}
                          alt={btn.text}
                          tintColor="white"
                        />
                        <Text
                          color="white"
                          fontSize="12px"
                          fontWeight="600"
                          lineHeight="14px"
                          fontFamily={GlobalStyles.text.fontFamily}
                        >
                          {btn.text}
                        </Text>
                      </HStack>
                    </Button>
                  ))}
                </HStack>
                <Box
                  borderBottomWidth={1}
                  borderBottomColor={GlobalStyles.colors.tableBorder}
                  mb={3}
                />
              </Box>

              <Box px={["4", "4", "0"]}>
                {activeView === "current" && !showPaymentForm && (
                  <CurrentServicesTable userId={userId} />
                )}
                {activeView === "available" && !showPaymentForm && (
                  <AvailableServicesTable
                    onPayClick={(s) => {
                      setSelectedService(s);
                      setShowPaymentForm(true);
                    }}
                    onTrialClick={(s) => {
                      setSelectedService(s);
                      setShowPaymentForm(true);
                    }}
                  />
                )}
                {activeView === "alerts" && <AlertsTable userId={userId} />}
                {activeView === "account" && !showPaymentForm && (
                  <AccountPaymentsTable
                    onSubscribePlan={(plan) => {
                      setPreselectedPlan(plan);
                      setSelectedService(null);
                      setShowPaymentForm(true);
                    }}
                  />
                )}
                {showPaymentForm && (
                  <PaymentForm
                    service={selectedService}
                    preselectedPlan={preselectedPlan}
                    loggedInUser={user}
                    onConfirm={() => {
                      setShowPaymentForm(false);
                      setActiveView("current");
                      setPreselectedPlan(null);
                    }}
                    onBack={() => {
                      setShowPaymentForm(false);
                      setPreselectedPlan(null);
                    }}
                  />
                )}
              </Box>
            </Box>
          </HStack>
        </Stack>
      </Center>
    </ScrollView>
  );
};

export default Subscription;
export const useUserServiceSubscriptions = (userId) => {
  const [serviceMap, setServiceMap] = useState({});
  const [isMember, setIsMember] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchServices = async () => {
    if (!userId) return;
    try {
      setIsLoading(true);
      const r = await axios.get(
        `${API_BASE_URL}/subscriptions/user/${userId}/services`,
      );
      const services = r.data?.services || r.data || [];
      const map = {};
      let hasMember = false;
      if (Array.isArray(services)) {
        services.forEach((s) => {
          map[s.service] = s;
          if (s.hasActiveSubscription) hasMember = true;
        });
      }
      setServiceMap(map);
      setIsMember(hasMember);
    } catch {
      setServiceMap({});
      setIsMember(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, [userId]);
  return { serviceMap, isMember, isLoading, refetch: fetchServices };
};

export const SubscribeServiceButton = ({
  serviceName,
  userId,
  isMember,
  serviceInfo,
  loggedInUser,
  onSubscribeSuccess,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const isActive = serviceInfo?.hasActiveSubscription === true;
  const displayName = SERVICE_LABELS[serviceName] || serviceName;

  const handlePress = () => {
    if (isActive) {
      setShowWarning(true);
    } else {
      setShowModal(true);
    }
  };

  return (
    <>
      <Button
        size="sm"
        onPress={handlePress}
        bg={isActive ? "gray.400" : GlobalStyles.colors.blue}
        borderRadius={6}
        opacity={isActive ? 0.85 : 1}
        _hover={{ bg: isActive ? "gray.500" : "#001544" }}
        _pressed={{ bg: isActive ? "gray.500" : "#001544" }}
        _text={{
          color: "white",
          fontFamily: GlobalStyles.text.fontFamily,
          fontSize: "13px",
        }}
      >
        {isActive ? "Subscribed ✓" : "Subscribe"}
      </Button>

      <AlreadySubscribedModal
        isOpen={showWarning}
        onClose={() => setShowWarning(false)}
        serviceName={displayName}
        onProceed={() => {
          setShowWarning(false);
          setShowModal(true);
        }}
      />

      <ServicePaymentModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        loggedInUser={loggedInUser}
        preselectedService={serviceName}
        onSuccess={(svc) => {
          setShowModal(false);
          onSubscribeSuccess && onSubscribeSuccess(svc);
        }}
      />
    </>
  );
};
